import { isEmpty } from 'lodash';
// Constants
import { errorLevel } from 'constants/errors';

import { treatErrorAs } from 'api/errors';

// constants
import { activityTypes, activityCategories } from 'constants/activities';

// selectors
import {
  getActiveFieldUuid,
  getActiveScriptType,
  getActiveUserId
} from 'current/selectors';
import { getActiveCropId } from 'current/currentSelectors';
import {
  getActivitiesByTypeAndDate,
  getActivityCategories,
  getActivityDefaultDates,
  getDefaultDateForActivityType
} from 'activities/selectors';
import {
  getSeasonActivityTypeForScriptType
} from 'seasons/selectors';
import {
  fetchActivitiesForFieldUuids,
  sendCreateNewActivities,
  sendUpdateExistingActivity
} from 'activities/senders';
import {
  processActivities,
  processActivity,
  processActivityCategories,
  processActivityDefaultDates
} from 'activities/processors';

// finders
import {
  findSeasonCodeByDateAndSeasonActivityType
} from 'seasons/finders';
import {
  findProductYears
} from 'settings/finders';
import { findFieldBoundariesByFieldUuid } from 'boundaries/finders';

export const findActivitiesForFieldUuid = ({ dispatch, getState, fieldUuid }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const userId = getActiveUserId(state);
    // TODO: caching, move parse/save to processor
    return fetchActivitiesForFieldUuids({ fieldUuids: [fieldUuid], userId })
    .then(rawActivities => processActivities({ dispatch, rawActivities }));
  });
};

export const findActivityCategories = ({ dispatch, getState }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();

    const storedActivityCategories = getActivityCategories(state);
    if( !isEmpty(storedActivityCategories) ) {
      return storedActivityCategories;
    }

    return processActivityCategories({
      dispatch,
      getState,
      rawCategories: activityCategories
    });
  });
};

/** @param params.activityType
 *  @param params.activityCategoryUuid
 *  @param params.date
 *  @param params.updatedDate
 */
export const findActivity = ({ dispatch, getState, params }) => {
  return Promise.resolve()
  .then(() => {
    if (!params.activityCategoryUuid) { return null; }

    const state = getState();
    const { activityType, activityCategoryUuid, updatedDate, date } = params;
    const scriptType = getActiveScriptType(state);
    const activityDate = updatedDate || date || getDefaultDateForActivityType(state, activityType);

    // check if already exist an activity for this date
    const existingActivity = getActivitiesByTypeAndDate(state, activityCategoryUuid, activityDate);

    let existingActivityDate;

    // if there is an existing activity
    if (existingActivity) {
      existingActivityDate = existingActivity.date;
      // if there is no updated date or the updated date is the same as the existing activity date
      if (!updatedDate || updatedDate === existingActivityDate)
      {
        // return the existing activity
        return existingActivity;
      }
    }

    const seasonActivityType = getSeasonActivityTypeForScriptType(state, scriptType);
    let cropId;
    // don't assign a crop id unless we need one, otherwise the season code will have a crop in it
    if (activityType === activityTypes.PLANTING) {
      cropId = getActiveCropId(state);
    }
    const fieldUuid = getActiveFieldUuid(state);
    const userId = getActiveUserId(state);

    let fetchedActivityUuid;
    return findFieldBoundariesByFieldUuid({ dispatch, getState, fieldUuid }).then(() => {
      return findSeasonCodeByDateAndSeasonActivityType({
        dispatch,
        seasonActivityType,
        cropId,
        date: activityDate,
        getState
      })
      .then((seasonCode) => {
        return Promise.resolve()
        .then(() => {
  
          if(
            (existingActivity && scriptType !== activityTypes.APPLICATION)
          ) {
            // update the existing activity with the new date
            return sendUpdateExistingActivity({
              ...existingActivity,
              season: seasonCode,
              date: activityDate,
              fieldUuid,
              userId
            });
          }
  
          return sendCreateNewActivities({ 
            activitiesParams: [{
              ...params,
              date: activityDate,
              fieldUuid,
              season: seasonCode,
              type: activityType,
              uuid: crypto.randomUUID(),
            }],
            userId
          });
        })
        .then((rawActivity) => processActivity({ date: activityDate, dispatch, fieldUuid, rawActivity }))
        .then((activityUuid) => {
          fetchedActivityUuid = activityUuid;
        })
        // then refetch/parse activities to include updated activity
        .then(() => findActivitiesForFieldUuid({ dispatch, getState, fieldUuid }))
        .then((activities) => activities?.byUuid?.[fetchedActivityUuid])
        .catch(treatErrorAs({ severity: errorLevel.INFO }));
      });
    });
  });
};

export const findActivityDefaultDates = ({ dispatch, getState }) => {
  const state = getState();
  const activityDefaultDates = getActivityDefaultDates(state);

  if (activityDefaultDates) {
    return Promise.resolve(activityDefaultDates);
  }

  return findProductYears({ dispatch, getState })
  .then((productYears) => processActivityDefaultDates({ dispatch, getState, productYears }) );
};
